import { Eye, Pin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminAnnouncement, resolveVideoEmbed } from "@/lib/announcements";
import { previewSeverityClass } from "./announcementHelpers";

type Props = {
  previewing: AdminAnnouncement | null;
  onClose: () => void;
};

export function AnnouncementPreviewDialog({ previewing, onClose }: Props) {
  return (
    <Dialog open={!!previewing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Preview as user
          </DialogTitle>
          <DialogDescription>
            How this announcement appears in the in-app notifications drawer.
          </DialogDescription>
        </DialogHeader>
        {previewing && (() => {
          const video = previewing.video_url ? resolveVideoEmbed(previewing.video_url) : null;
          return (
            <article
              className={cn(
                "rounded-lg border p-4",
                previewing.is_pinned ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card",
                "ring-1 ring-primary/30",
              )}
            >
              <header className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    {previewing.is_pinned && <Pin className="h-3 w-3 text-amber-500" />}
                    <Badge variant="secondary" className={cn("text-[10px]", previewSeverityClass[previewing.severity])}>
                      {previewing.severity}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {previewing.published_at
                        ? new Date(previewing.published_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })
                        : "Not yet published"}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold leading-snug">{previewing.title}</h3>
                </div>
              </header>
              {video && (
                <div className="mb-2 aspect-video w-full overflow-hidden rounded-md border bg-muted">
                  {video.kind === "iframe" ? (
                    <iframe
                      src={video.src}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={previewing.title}
                    />
                  ) : (
                    <video src={video.src} controls className="h-full w-full" />
                  )}
                </div>
              )}
              {previewing.body_md && (
                <p className="whitespace-pre-wrap text-sm text-foreground/90">{previewing.body_md}</p>
              )}
            </article>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
